/* eslint-disable max-lines */
import { mergeWithOptimisticPlaceholders } from '@helpers/merge-with-optimistic-placeholders';
import { createReducer, on } from '@ngrx/store';
import { FlattenedSequence } from '@pixacare/dtos';
import { getNextPagedCollection, getPreviousPagedCollection } from '../../models/helpers/pagination-helper';
import { lifeCycleActions } from '../life-cycle/life-cycle.actions';
import { telemonitoringsActions } from '../telemonitorings/telemonitorings.actions';
import { isOptimisticMedia } from './is-optimistic-media';
import { mediaContentKey } from './media-content-key';
import { StoreSequence } from './models/store-sequence';
import { sequencesActions } from './sequences.actions';
import { initialSequencesState, SequencesState } from './sequences.state';

const flattenedSequenceToStoreSequence = (flattenedSequence: FlattenedSequence): StoreSequence => ({
  ...flattenedSequence.sequenceInstance,
  medias: [...flattenedSequence.medias].sort((firstMedia, secondMedia) => firstMedia.id - secondMedia.id),
  clientCode: flattenedSequence.clientCode,
  labelIds: flattenedSequence.labels.map((label) => label.id),
});

const flattenedSequencesToStoreSequences = (
  flattenedSequences: FlattenedSequence[],
): { [sequenceId: number]: StoreSequence } =>
  Object.fromEntries(
    flattenedSequences.map((flattenedSequence) => [
      flattenedSequence.sequenceInstance.id,
      flattenedSequenceToStoreSequence(flattenedSequence),
    ]),
  );

const sequenceSortByCreatedOn = (s1: StoreSequence, s2: StoreSequence): number =>
  (s2?.createdOn.getTime() ?? 0) - (s1?.createdOn.getTime() ?? 0);

const sequenceSort = (
  sequenceId1: number,
  sequenceId2: number,
  sequences: { [sequenceId: number]: StoreSequence },
): number => sequenceSortByCreatedOn(sequences[sequenceId1], sequences[sequenceId2]);

export const sequencesReducer = createReducer(
  initialSequencesState,

  on(lifeCycleActions.resetState, sequencesActions.resetSequencesState, () => initialSequencesState),

  on(sequencesActions.updateFlattenedSequenceSuccess, (state, { sequence }): SequencesState => {
    const sequenceId = sequence.sequenceInstance.id;

    return {
      ...state,
      sequences: {
        ...state.sequences,
        data: {
          ...state.sequences.data,
          [sequenceId]: {
            ...state.sequences.data?.[sequenceId],
            ...flattenedSequenceToStoreSequence(sequence),
          },
        },
        sorted: [
          ...state.sequences.sorted,
          ...(state.sequences.sorted.indexOf(sequenceId) < 0 ? [sequenceId] : []),
        ].sort((sequenceId1, sequenceId2) => sequenceSort(sequenceId1, sequenceId2, state.sequences.data)),
      },
    };
  }),

  on(sequencesActions.updateSequenceSuccess, (state, { updatedSequence }): SequencesState => ({
    ...state,
    sequences: {
      ...state.sequences,
      data: {
        ...state.sequences.data,
        [updatedSequence.id]: {
          ...state.sequences.data[updatedSequence.id],
          ...updatedSequence,
        },
      },
      sorted: [
        ...state.sequences.sorted,
        ...(state.sequences.sorted.indexOf(updatedSequence.id) < 0 ? [updatedSequence.id] : []),
      ].sort((sequenceId1, sequenceId2) => sequenceSort(sequenceId1, sequenceId2, state.sequences.data)),
    },
  })),

  on(sequencesActions.getNextUserSequences, (state, { options: { reset } }): SequencesState => ({
    ...state,
    sequences: {
      ...state.sequences,
      context: {
        ...state.sequences.context,
        isNextPageLoading: true,
      },
      ...(reset && { sorted: [], data: null }),
    },
    ...(reset && { isLoaded: false }),
  })),

  on(sequencesActions.getPreviousUserSequences, (state, { options: { reset } }): SequencesState => ({
    ...state,
    sequences: {
      ...state.sequences,
      context: {
        ...state.sequences.context,
        isPreviousPageLoading: true,
      },
      ...(reset && { data: null }),
    },
    ...(reset && { isLoaded: false }),
  })),

  on(sequencesActions.getNextUserSequencesSuccess, (state, { reset, collection }): SequencesState => ({
    ...state,
    isLoaded: true,
    sequences: getNextPagedCollection(
      {
        currentCollection: state.sequences, //  PagedCollection<{ [sequenceId: number]: StoreSequence }>;
        nextCollection: collection, // PagedResponse<FlattenedSequence>
      },
      {
        reset,
        dataReducer: flattenedSequencesToStoreSequences,
        dataIdentifier: 'sequenceInstance.id',
      },
    ),
  })),

  on(sequencesActions.getPreviousUserSequencesSuccess, (state, { collection }): SequencesState => ({
    ...state,
    sequences: getPreviousPagedCollection(
      {
        currentCollection: state.sequences,
        nextCollection: collection,
      },
      {
        dataReducer: flattenedSequencesToStoreSequences,
        dataIdentifier: 'sequenceInstance.id',
      },
    ),
  })),

  on(sequencesActions.loadSequences, (state, { data }): SequencesState => ({
    ...state,
    sequences: {
      ...state.sequences,
      data: {
        ...state.sequences.data,
        ...flattenedSequencesToStoreSequences(data),
      },
    },
  })),

  on(sequencesActions.updateSequenceMediaSuccess, (state, { sequenceId, updatedMedia }): SequencesState => {
    if (state.sequences.data) {
      return {
        ...state,
        sequences: {
          ...state.sequences,
          data: {
            ...state.sequences.data,
            [sequenceId]: {
              ...state.sequences.data[sequenceId],
              medias: (state.sequences.data[sequenceId] || {}).medias?.map((media) =>
                media.id === updatedMedia.id ? updatedMedia : media,
              ),
            },
          },
        },
      };
    } else {
      return state;
    }
  }),

  on(sequencesActions.createSequenceSuccess, (state, { sequence }): SequencesState => {
    const newState = {
      ...state,
      sequences: {
        ...state.sequences,
        data: {
          ...state.sequences.data,
          [sequence.sequenceInstance.id]: flattenedSequenceToStoreSequence(sequence),
        },
      },
    };

    return {
      ...newState,
      sequences: {
        ...newState.sequences,
        sorted: [sequence.sequenceInstance.id, ...state.sequences.sorted].sort((sequenceId1, sequenceId2) =>
          sequenceSort(sequenceId1, sequenceId2, newState.sequences.data),
        ),
      },
    };
  }),

  on(sequencesActions.addSequenceMediaSuccess, (state, { sequenceId, updatedMedia }): SequencesState => ({
    ...state,
    sequences: {
      ...state.sequences,
      data: {
        ...state.sequences.data,
        [sequenceId]: {
          ...state.sequences.data[sequenceId],
          medias: [...state.sequences.data[sequenceId].medias, updatedMedia],
        },
      },
    },
  })),

  on(sequencesActions.deleteSequenceSuccess, (state, { sequenceId }): SequencesState => {
    const { [sequenceId]: deletedSequence, ...data } = state.sequences.data;

    return {
      ...state,
      sequences: {
        ...state.sequences,
        data,
        sorted: state.sequences.sorted.filter((sId) => sequenceId !== sId),
      },
    };
  }),

  // Inserts placeholder medias for files being uploaded, appending only those not already present
  // (deduplicated by content key). No-op when the sequence isn't loaded — placeholders only make
  // sense on a sequence currently in the store.
  on(sequencesActions.insertOptimisticMedias, (state, { clientCode, sequenceId, medias }): SequencesState => {
    const sequence = state.sequences.data?.[sequenceId];
    // Sequence state isn't client-scoped. Early return if sequence is from an other client.
    if (!sequence || sequence.clientCode !== clientCode) {
      return state;
    }

    const existingKeys = new Set(sequence.medias.map(mediaContentKey));
    const newPlaceholders = medias.filter((media) => !existingKeys.has(mediaContentKey(media)));
    if (newPlaceholders.length === 0) {
      return state;
    }

    return {
      ...state,
      sequences: {
        ...state.sequences,
        data: {
          ...state.sequences.data,
          [sequenceId]: {
            ...sequence,
            medias: [...sequence.medias, ...newPlaceholders],
          },
        },
      },
    };
  }),

  // Drops the optimistic placeholder matching the content key — used when an interrupted upload is
  // abandoned, so its gray square leaves the gallery right away instead of lingering until the next
  // sequence fetch. Only optimistic placeholders are ever removed; a real media is left alone.
  on(
    sequencesActions.removeOptimisticMedia,
    (state, { clientCode, sequenceId, originalFilename, size }): SequencesState => {
      const sequence = state.sequences.data?.[sequenceId];
      if (!sequence || sequence.clientCode !== clientCode) {
        return state;
      }

      const removedKey = mediaContentKey({ originalFilename, size });
      const medias = sequence.medias.filter(
        (media) => !(isOptimisticMedia(media) && mediaContentKey(media) === removedKey),
      );
      if (medias.length === sequence.medias.length) {
        return state;
      }

      return {
        ...state,
        sequences: {
          ...state.sequences,
          data: {
            ...state.sequences.data,
            [sequenceId]: { ...sequence, medias },
          },
        },
      };
    },
  ),

  // Merges the attached medias in rather than replacing the whole array, so a concurrent upload of
  // another type (its placeholders still in flight) is never clobbered.
  on(sequencesActions.attachSequenceMediasSuccess, (state, { sequenceId, medias: sequenceMedias }): SequencesState => {
    const sequence = state.sequences.data?.[sequenceId];
    if (!sequence) {
      return state;
    }

    return {
      ...state,
      sequences: {
        ...state.sequences,
        data: {
          ...state.sequences.data,
          [sequenceId]: {
            ...sequence,
            medias: mergeWithOptimisticPlaceholders(sequenceMedias, sequence.medias),
          },
        },
      },
    };
  }),

  on(sequencesActions.deleteSequenceMediaSuccess, (state, { sequenceId, mediaId }): SequencesState => ({
    ...state,
    sequences: {
      ...state.sequences,
      data: {
        ...state.sequences.data,
        [sequenceId]: {
          ...state.sequences.data[sequenceId],
          medias: state.sequences.data[sequenceId].medias.filter((media) => media.id !== mediaId),
        },
      },
    },
  })),

  on(telemonitoringsActions.addTelemonitoring, (state, { telemonitoring, sequenceIds }): SequencesState => ({
    ...state,
    sequences: {
      ...state.sequences,
      data: {
        ...state.sequences.data,
        ...Object.fromEntries(
          sequenceIds.map((sequenceId) => [
            sequenceId,
            {
              ...state.sequences.data[sequenceId],
              telemonitoringId: telemonitoring.id,
            },
          ]),
        ),
      },
    },
  })),

  on(telemonitoringsActions.deleteTelemonitoringSuccess, (state, { telemonitoringId }): SequencesState => ({
    ...state,
    sequences: {
      ...state.sequences,
      data: Object.fromEntries(
        Object.values(state.sequences.data)
          .filter(
            (sequence) =>
              !(sequence.telemonitoringId === telemonitoringId && !state.sequences.sorted.includes(sequence.id)),
          )
          .map((sequence) => [sequence.id, state.sequences.data[sequence.id]]),
      ),
    },
  })),

  on(sequencesActions.deleteSadmEntityFromSequences, (state, { sadmEntityId }): SequencesState => ({
    ...state,
    sequences: {
      ...state.sequences,
      data: state.sequences.data
        ? Object.fromEntries(
            Object.values(state.sequences.data).map((sequence) => [
              sequence.id,
              sequence.sadmEntityId === sadmEntityId
                ? {
                    ...state.sequences.data[sequence.id],
                    sadmEntityId: null,
                    formAnswerId: null,
                  }
                : state.sequences.data[sequence.id],
            ]),
          )
        : state.sequences.data,
    },
  })),

  on(sequencesActions.reviewSequenceSuccess, (state, { sequenceId, reviewedBy, reviewedAt, isReviewed }) => ({
    ...state,
    sequences: {
      ...state.sequences,
      data: {
        ...state.sequences.data,
        [sequenceId]: {
          ...state.sequences.data[sequenceId],
          sequenceReview: {
            ...state.sequences.data[sequenceId].sequenceReview,
            sequenceId,
            reviewedBy,
            reviewedAt,
            isReviewed,
          },
        },
      },
    },
  })),

  on(sequencesActions.getSequenceSuccess, (state, { sequenceId, sequence }): SequencesState => ({
    ...state,
    sequences: {
      ...state.sequences,
      data: {
        ...state.sequences.data,
        [sequenceId]: flattenedSequenceToStoreSequence(sequence),
      },
    },
  })),
);
